import './SonarSelector.scss';

import { BaseSelectRef } from 'rc-select';
import classNames from 'classnames';
import { EditBox } from './EditBox';
import { getClosestRow } from '@lib/popups';
import { haltClickEventPropagation } from '@lib/events';
import { stringContainsSubstringCaseInsensitive } from '@sonar-software/sonar-ts-lib';

import { AntdOptionType, AntdSelectValue } from '@sonar-software/sonar-ts-types';
import { Else, If, Then } from 'react-if';
import { ProcessingState, useCurrentUser } from '@hooks';
import React, { ReactNode, useCallback, useRef } from 'react';
import { Row, Select, SelectProps } from 'antd';

const handleOnSearch = (input: string, option?: AntdOptionType) =>
	stringContainsSubstringCaseInsensitive(String(option?.searchValue ?? option?.label), input);

export type SonarSelectorProps<T extends AntdSelectValue = AntdSelectValue> =
	Omit<SelectProps<T, AntdOptionType>, 'direction' | 'classNames' | 'popupClassName'> & {
		allowReadonly?: boolean;
		boxless?: boolean;
		direction?: 'horizontal' | 'vertical';
		isSalesforce?: boolean;
		isSpreadsheet?: boolean;
		onChange?: (values: T | undefined) => void;
		popupClassName?: string;
		readOnlyDisplay?: ReactNode;
		showArrow?: boolean;
		showSearch?: boolean;
		state?: ProcessingState;
};

export const SonarSelector: React.FC<SonarSelectorProps> = ({
	allowClear = true,
	allowReadonly,
	boxless,
	className,
	defaultValue,
	direction = 'vertical',
	disabled,
	getPopupContainer,
	isSalesforce,
	isSpreadsheet,
	mode,
	onChange,
	popupClassName,
	readOnlyDisplay,
	showArrow,
	showSearch = true,
	state,
	value,
	...rest
}) => {
	const selectRef = useRef<BaseSelectRef>(null);
	const { isReadOnly: isReadOnlyUser } = useCurrentUser();
	const isReadOnly = isReadOnlyUser && !allowReadonly;

	const handleOnChange = useCallback((changeValue: AntdSelectValue | undefined) => {
		if (onChange) {
			onChange(changeValue);
		}

		if (mode === 'tags') {
			selectRef.current?.blur();
		}
	}, [ mode, onChange ]);

	const selectorClassNames = classNames('sonar-selector', className, {
		boxless,
		horizontal: direction === 'horizontal',
		processing: state?.isProcessing,
		readonly: isReadOnly,
		vertical: direction === 'vertical'
	});

	return (
		<EditBox
			className={ selectorClassNames }
			isReadOnly={ isReadOnly || disabled }
			isSalesforce={ isSalesforce }
			isSpreadsheet={ isSpreadsheet }
			readOnlyHover
			selectionRef={ selectRef }
			state={ state }>
			<Row
				onClick={ haltClickEventPropagation }
				wrap={ false }>
				<If condition={ !isReadOnly }>
					<Then>
						<Select
							{ ...rest }
							allowClear={ allowClear }
							classNames={ {
								popup: {
									root: classNames('sonar-selector-popup', popupClassName)
								}
							} }
							defaultValue={ defaultValue }
							disabled={ disabled }
							filterOption={ handleOnSearch }
							getPopupContainer={ getPopupContainer ?? getClosestRow }
							mode={ mode }
							onChange={ handleOnChange }
							optionLabelProp='optionLabel'
							ref={ selectRef }
							removeIcon={ null }
							showAction={ [ 'focus' ] }
							showSearch={ showSearch }
							suffixIcon={ showArrow ? undefined : null }
							value={ value ?? defaultValue } />
					</Then>
					<Else>
						{ readOnlyDisplay }
					</Else>
				</If>
			</Row>
		</EditBox>
	);
};
